import { useEffect, useRef, useState, useMemo } from 'react';
import { useSessionStorageState } from 'ahooks';
import type { Coordinate } from '~/modules/location/hook/use-current-coordinates';
import { getCoordinatesByBrowser } from '~/modules/location/hook/use-current-coordinates';
import { useCurrentCoordinates } from '~/modules/location/hook/use-current-coordinates';
import { coordinateDistanceKm } from '@archipro-website/localisation';
import { useLogger } from '@archipro-website/logger/client';

interface BasicLocationDateType {
    MapCoordinates?: Coordinate;
    distance?: number;
}

const COORD_SESSION_STORAGE_NAME = 'visitor-current-coordinates';

export const useHasSavedCoords = () => {
    const [savedCoords] = useSessionStorageState<Coordinate | undefined>(
        COORD_SESSION_STORAGE_NAME,
    );
    return !!savedCoords;
};

export const useLocationsSortByDistance = <
    T extends BasicLocationDateType,
>(opts: {
    locations: T[];
    existingUserCoordinates?: Coordinate;
    skip?: boolean;
}): {
    sortedLocations: T[];
    userLocationLoaded: boolean;
    calculateDistance: () => Promise<boolean>;
    refetchSortedLocations: () => Promise<T[]>;
} => {
    const logger = useLogger();
    const { locations, existingUserCoordinates, skip } = opts;
    const [newLocations, setNewLocations] = useState<T[]>(locations || []);
    const [userLocationLoaded, setUserLocationLoaded] = useState(false);

    const [savedCoords, setSavedCoords] = useSessionStorageState<
        Coordinate | undefined
    >(COORD_SESSION_STORAGE_NAME);

    const isBrowserGeoEnabled =
        typeof navigator !== 'undefined' && 'geolocation' in navigator;

    const defaultCoords =
        existingUserCoordinates ||
        (isBrowserGeoEnabled ? savedCoords : undefined);

    const { userCoordinates, findUserCoordinates, loaded } =
        useCurrentCoordinates(defaultCoords, skip);

    const isDistanceUpdatedRef = useRef(false);

    const isValidUserCoordinates = isValidCoordinates(userCoordinates);

    const refetchSortedLocations = async () => {
        try {
            const userGeoCoords = await getCoordinatesByBrowser();
            if (userGeoCoords && isValidCoordinates(userGeoCoords)) {
                setSavedCoords(userGeoCoords);
                return addDistanceToLocations(locations, userGeoCoords);
            }
        } catch (error) {
            logger.error('refetchSortedLocations err', error);
        }
        return locations;
    };

    const calculateDistance = async () => {
        const locations = await refetchSortedLocations();
        setNewLocations(locations);
        return locationHasDistance(locations);
    };

    useEffect(() => {
        if (!isValidUserCoordinates) {
            if (loaded) {
                setUserLocationLoaded(true);
            } else {
                findUserCoordinates();
            }
        } else {
            setUserLocationLoaded(true);
        }
    }, [findUserCoordinates, loaded, isValidUserCoordinates]);

    useEffect(() => {
        if (
            isValidUserCoordinates &&
            isValidCoordinates(locations[0]?.MapCoordinates) &&
            !locationHasDistance(locations) &&
            !isDistanceUpdatedRef.current
        ) {
            const tempLocations = addDistanceToLocations<T>(
                locations,
                userCoordinates,
            );

            setNewLocations(tempLocations);
            isDistanceUpdatedRef.current = true;
            if (isBrowserGeoEnabled) {
                setSavedCoords(userCoordinates);
            }
        }
    }, [
        locations,
        userCoordinates,
        isValidUserCoordinates,
        isBrowserGeoEnabled,
        setSavedCoords,
    ]);

    // Memoize sortedLocations to prevent recreation on every render
    const memoizedSortedLocations = useMemo(() => {
        const result = sortLocationsByDistance(newLocations);
        return result;
    }, [newLocations]);

    return {
        sortedLocations: memoizedSortedLocations,
        userLocationLoaded,
        calculateDistance,
        refetchSortedLocations,
    };
};

/**
 * Adds distance from user coordinates to each location and sorts them by distance.
 * Creates a deep copy of locations array to avoid mutating the original.
 * @param locations Array of locations to add distances to
 * @param userCoordinates User's latitude/longitude coordinates
 * @returns New array of locations with distances added and sorted by distance
 */
const addDistanceToLocations = <T extends BasicLocationDateType>(
    locations: T[],
    userCoordinates: Coordinate,
): T[] => {
    const newLocations = JSON.parse(JSON.stringify(locations));
    newLocations.map((locationData: T) => {
        locationData.distance = coordinateDistanceKm(
            locationData?.MapCoordinates?.Latitude || 0,
            locationData?.MapCoordinates?.Longitude || 0,
            userCoordinates.Latitude || 0,
            userCoordinates.Longitude || 0,
        );
        return locationData;
    });
    return sortLocationsByDistance(newLocations);
};

export const isValidCoordinates = (MapCoordinates?: Coordinate): boolean => {
    if (!MapCoordinates) {
        return false;
    }
    return !(MapCoordinates.Latitude === 0 && MapCoordinates.Longitude === 0);
};

export const locationHasDistance = (
    locations: BasicLocationDateType[],
): boolean => {
    return (
        !!locations?.[0] &&
        Object.prototype.hasOwnProperty.call(locations[0], 'distance')
    );
};

/**
 * Sorts locations by distance in ascending order.
 * Locations without distance are treated as having 0 distance.
 */
const sortLocationsByDistance = <T extends BasicLocationDateType>(
    locations: T[],
): T[] => {
    if (locationHasDistance(locations)) {
        return [...locations].sort(
            (a: T, b: T) => (a?.distance || 0) - (b?.distance || 0),
        );
    }
    return locations;
};
